/**
 * Energy Equivalence Visualization
 * Visualizes the equation E_np = M_np * C_np^2 from the Theory of Sovereign Reflectivity
 */

// Main p5.js sketch
const energyEquivalenceSketch = (p) => {
    // Configuration
    const config = {
        colors: {
            primary: [44, 62, 80], // #2C3E50 (Deep indigo)
            secondary: [142, 68, 173], // #8E44AD (Cosmic purple)
            accent: [241, 196, 15], // #F1C40F (Gold)
            background: [18, 18, 18], // #121212 (Near-black)
            text: [245, 245, 245] // #F5F5F5 (Off-white)
        },
        particleCount: 100,
        showComparison: false
    };

    // Variables for the equation
    let mNp = 1.0; // Nonphysical mass
    let cNp = 1.0; // Consciousness current (relative to c)
    let eNp = 1.0; // Nonphysical energy (calculated)

    // Physical equivalents for comparison
    let m = 1.0; // Physical mass (inverse of mNp)
    let e = 1.0; // Physical energy (calculated)

    // UI elements
    let mNpSlider, cNpSlider;
    let mNpValue, cNpValue, eNpValue;
    let resetButton, toggleComparisonButton;

    // Particles for visualization
    let particles = [];

    // Setup function
    p.setup = function () {
        try {
            // Check if the canvas element exists
            const canvasContainer = document.getElementById('energy-equivalence-canvas');
            if (!canvasContainer) {
                console.warn('Energy equivalence canvas container not found, skipping setup');
                p.noLoop();
                return;
            }

            // Create canvas
            const canvas = p.createCanvas(p.windowWidth > 800 ? 800 : p.windowWidth - 40, 500);
            canvas.parent('energy-equivalence-canvas');

            // Initialize UI elements from DOM with error handling
            mNpSlider = document.getElementById('m-np-slider');
            cNpSlider = document.getElementById('c-np-slider');
            mNpValue = document.getElementById('m-np-value');
            cNpValue = document.getElementById('c-np-value');
            eNpValue = document.getElementById('e-np-value');
            resetButton = document.getElementById('reset-button');
            toggleComparisonButton = document.getElementById('toggle-comparison');

            // Check if all required elements exist
            if (!mNpSlider || !cNpSlider || !mNpValue || !cNpValue || !eNpValue || !resetButton || !toggleComparisonButton) {
                console.warn('Some UI elements for energy equivalence visualization not found');
                // Continue with available elements
            }

            // Add event listeners with error handling
            if (mNpSlider) mNpSlider.addEventListener('input', updateMNp);
            if (cNpSlider) cNpSlider.addEventListener('input', updateCNp);
            if (resetButton) resetButton.addEventListener('click', resetValues);
            if (toggleComparisonButton) toggleComparisonButton.addEventListener('click', toggleComparison);

            // Initialize particles
            createParticles();

            // Calculate initial energy
            calculateEnergy();
        } catch (err) {
            console.error('Error in energy equivalence setup:', err);
            p.noLoop();
        }
    };

    // Draw function
    p.draw = function () {
        p.background(config.colors.background);

        if (config.showComparison) {
            drawComparisonView();
        } else {
            drawStandardView();
        }
    };

    // Create particles for visualization
    function createParticles() {
        particles = [];
        for (let i = 0; i < config.particleCount; i++) {
            particles.push({
                x: p.random(p.width),
                y: p.random(p.height),
                size: p.random(3, 8),
                speed: p.random(0.5, 2),
                angle: p.random(p.TWO_PI),
                color: p.random() > 0.8 ? config.colors.accent : config.colors.secondary
            });
        }
    }

    // Update nonphysical mass from slider
    function updateMNp() {
        try {
            // Get value from slider if it exists
            if (mNpSlider) {
                mNp = parseFloat(mNpSlider.value);
            }

            // Update display if it exists
            if (mNpValue) {
                mNpValue.textContent = mNp.toFixed(1);
            }

            // Update inverse physical mass
            m = 1 / mNp;

            // Recalculate energy
            calculateEnergy();
        } catch (err) {
            console.error('Error updating nonphysical mass:', err);
        }
    }

    // Update consciousness current from slider
    function updateCNp() {
        try {
            // Get value from slider if it exists
            if (cNpSlider) {
                cNp = parseFloat(cNpSlider.value);
            }

            // Update display if it exists
            if (cNpValue) {
                cNpValue.textContent = cNp.toFixed(1);
            }

            // Recalculate energy
            calculateEnergy();
        } catch (err) {
            console.error('Error updating consciousness current:', err);
        }
    }

    // Calculate nonphysical energy based on E_np = M_np * C_np^2
    function calculateEnergy() {
        try {
            // Calculate nonphysical energy
            eNp = mNp * (cNp * cNp);

            // Update display if element exists
            if (eNpValue) {
                eNpValue.textContent = eNp.toFixed(2) + ' units';
            }

            // Calculate physical energy E = mc^2
            e = m * (1 * 1); // c^2 = 1 for simplicity
        } catch (err) {
            console.error('Error calculating energy:', err);
            // Set default values in case of error
            eNp = 1.0;
            e = 1.0;
        }
    }

    // Reset all values to defaults
    function resetValues() {
        try {
            if (mNpSlider) mNpSlider.value = 1.0;
            if (cNpSlider) cNpSlider.value = 1.0;

            // Reset internal values
            mNp = 1.0;
            cNp = 1.0;

            // Update displays
            if (mNpValue) mNpValue.textContent = mNp.toFixed(1);
            if (cNpValue) cNpValue.textContent = cNp.toFixed(1);

            // Recalculate energy
            calculateEnergy();
        } catch (err) {
            console.error('Error resetting values:', err);
        }
    }

    // Toggle comparison view
    function toggleComparison() {
        try {
            config.showComparison = !config.showComparison;
            if (toggleComparisonButton) {
                toggleComparisonButton.textContent = config.showComparison ? 'Hide Comparison' : 'Toggle Comparison';
            }
        } catch (err) {
            console.error('Error toggling comparison view:', err);
        }
    }

    // Draw standard visualization view
    function drawStandardView() {
        // Draw background grid
        drawGrid();

        // Draw particles based on energy
        updateAndDrawParticles();

        // Draw central mass representation
        drawMassRepresentation();

        // Draw energy field
        drawEnergyField();

        // Draw labels and equation
        drawLabels();
    }

    // Draw comparison view (physical vs nonphysical)
    function drawComparisonView() {
        // Draw dividing line
        p.stroke(config.colors.accent);
        p.strokeWeight(2);
        p.line(p.width / 2, 0, p.width / 2, p.height);

        // Draw titles
        p.noStroke();
        p.fill(config.colors.text);
        p.textAlign(p.CENTER, p.CENTER);
        p.textSize(16);
        p.text("Nonphysical Domain", p.width * 0.25, 30);
        p.text("Physical Domain", p.width * 0.75, 30);

        // Draw equations
        p.fill(config.colors.accent);
        p.textSize(20);
        p.text(`E_np = ${mNp.toFixed(1)} × ${cNp.toFixed(1)}² = ${eNp.toFixed(2)}`, p.width * 0.25, 60);
        p.text(`E = ${m.toFixed(2)} × c² = ${e.toFixed(2)}`, p.width * 0.75, 60);

        // Define center Y for both sides
        const centerY = p.height * 0.5;

        // Draw nonphysical side (left side)
        // Draw mass representation on nonphysical side
        const npCenterX = p.width * 0.25;

        // Draw nonphysical mass
        p.noStroke();
        p.fill(config.colors.secondary[0], config.colors.secondary[1], config.colors.secondary[2], 150);
        const npRadius = 30 + mNp * 10;
        p.ellipse(npCenterX, centerY, npRadius * 2);

        // Draw energy field for nonphysical side
        p.noFill();
        for (let i = 0; i < 5; i++) {
            const alpha = p.map(i, 0, 5, 100, 20);
            const radius = npRadius + (eNp * 15 * (i + 1));
            p.stroke(config.colors.accent[0], config.colors.accent[1], config.colors.accent[2], alpha);
            p.strokeWeight(2);
            p.ellipse(npCenterX, centerY, radius * 2);
        }

        // Draw physical side (right side)
        // Draw mass representation on physical side
        const pCenterX = p.width * 0.75;

        // Draw physical mass
        p.noStroke();
        p.fill(config.colors.primary[0], config.colors.primary[1], config.colors.primary[2], 150);
        const pRadius = 30 + m * 10;
        p.ellipse(pCenterX, centerY, pRadius * 2);

        // Draw energy field for physical side
        p.noFill();
        for (let i = 0; i < 5; i++) {
            const alpha = p.map(i, 0, 5, 100, 20);
            const radius = pRadius + (e * 15 * (i + 1));
            p.stroke(255, 255, 255, alpha);
            p.strokeWeight(2);
            p.ellipse(pCenterX, centerY, radius * 2);
        }

        // Draw inverse relationship arrow
        p.stroke(config.colors.accent);
        p.strokeWeight(2);
        p.fill(config.colors.accent);
        p.drawArrow(p.width * 0.3, p.height * 0.8, p.width * 0.7, p.height * 0.8);
        p.drawArrow(p.width * 0.7, p.height * 0.85, p.width * 0.3, p.height * 0.85);

        p.noStroke();
        p.fill(config.colors.text);
        p.textSize(14);
        p.text("Inverse Relationship", p.width * 0.5, p.height * 0.75);
        p.textSize(12);
        p.text("As nonphysical mass increases, physical mass decreases", p.width * 0.5, p.height * 0.9);
    }

    // Draw background grid
    function drawGrid() {
        p.stroke(config.colors.primary[0], config.colors.primary[1], config.colors.primary[2], 50);
        p.strokeWeight(1);

        const gridSize = 30;
        for (let x = 0; x < p.width; x += gridSize) {
            p.line(x, 0, x, p.height);
        }

        for (let y = 0; y < p.height; y += gridSize) {
            p.line(0, y, p.width, y);
        }
    }

    // Update and draw particles
    function updateAndDrawParticles() {
        const centerX = p.width / 2;
        const centerY = p.height / 2;

        // Scale particle count based on energy
        const activeParticles = Math.min(config.particleCount, Math.floor(config.particleCount * (eNp / 10)));

        for (let i = 0; i < activeParticles; i++) {
            const particle = particles[i];

            // Update position
            particle.x += Math.cos(particle.angle) * particle.speed * (cNp * 0.5);
            particle.y += Math.sin(particle.angle) * particle.speed * (cNp * 0.5);

            // Wrap around edges
            if (particle.x < 0) particle.x = p.width;
            if (particle.x > p.width) particle.x = 0;
            if (particle.y < 0) particle.y = p.height;
            if (particle.y > p.height) particle.y = 0;

            // Draw particle
            p.noStroke();
            p.fill(particle.color[0], particle.color[1], particle.color[2], 150);
            p.ellipse(particle.x, particle.y, particle.size);

            // Draw connection to center if close enough
            const distance = p.dist(particle.x, particle.y, centerX, centerY);
            if (distance < 150 + (eNp * 10)) {
                p.stroke(particle.color[0], particle.color[1], particle.color[2], 50);
                p.strokeWeight(1);
                p.line(particle.x, particle.y, centerX, centerY);
            }
        }
    }

    // Draw central mass representation
    function drawMassRepresentation() {
        const centerX = p.width / 2;
        const centerY = p.height / 2;

        // Draw mass as a circle with size based on M_np
        p.noStroke();
        p.fill(config.colors.secondary[0], config.colors.secondary[1], config.colors.secondary[2], 150);
        const radius = 30 + mNp * 10;
        p.ellipse(centerX, centerY, radius * 2);

        // Add pulsing effect
        p.noFill();
        p.stroke(config.colors.secondary[0], config.colors.secondary[1], config.colors.secondary[2], 80);
        p.strokeWeight(2);
        const pulseSize = radius + 10 + Math.sin(p.frameCount * 0.05) * 5;
        p.ellipse(centerX, centerY, pulseSize * 2);
    }

    // Draw energy field
    function drawEnergyField() {
        const centerX = p.width / 2;
        const centerY = p.height / 2;

        // Draw energy as concentric circles
        p.noFill();
        for (let i = 0; i < 5; i++) {
            const alpha = p.map(i, 0, 5, 100, 20);
            const radius = 50 + (eNp * 15 * (i + 1));
            p.stroke(config.colors.accent[0], config.colors.accent[1], config.colors.accent[2], alpha);
            p.strokeWeight(2);
            p.ellipse(centerX, centerY, radius * 2);
        }

        // Add flowing energy effect
        p.push();
        p.translate(centerX, centerY);
        p.rotate(p.frameCount * 0.01);

        p.stroke(config.colors.accent[0], config.colors.accent[1], config.colors.accent[2], 150);
        p.strokeWeight(2);
        p.noFill();

        // Draw flowing energy lines
        for (let i = 0; i < 12; i++) {
            const angle = i * p.TWO_PI / 12;
            const innerRadius = 40 + mNp * 10;
            const outerRadius = innerRadius + eNp * 20;

            p.push();
            p.rotate(angle);
            p.beginShape();
            for (let r = innerRadius; r < outerRadius; r += 5) {
                const x = r;
                const y = Math.sin(r * 0.1 + p.frameCount * 0.1) * 5;
                p.vertex(x, y);
            }
            p.endShape();
            p.pop();
        }

        p.pop();
    }

    // Draw labels and equation
    function drawLabels() {
        p.noStroke();
        p.fill(config.colors.text);
        p.textAlign(p.CENTER, p.CENTER);

        // Draw equation at the top
        p.textSize(24);
        p.fill(config.colors.accent);
        p.text(`E_np = M_np × C_np²`, p.width / 2, 30);

        // Draw current values
        p.textSize(16);
        p.fill(config.colors.text);
        p.text(`M_np = ${mNp.toFixed(1)}   C_np = ${cNp.toFixed(1)}   E_np = ${eNp.toFixed(2)}`, p.width / 2, 60);

        // Draw labels for visualization elements
        p.textSize(14);
        p.text("Nonphysical Mass", p.width / 2, p.height / 2 + 80);
        p.text("Energy Field", p.width / 2, p.height / 2 - 80);
    }

    // Helper function to draw arrows
    p.drawArrow = function (x1, y1, x2, y2) {
        p.line(x1, y1, x2, y2);

        const angle = p.atan2(y2 - y1, x2 - x1);
        const arrowSize = 10;

        p.push();
        p.translate(x2, y2);
        p.rotate(angle);
        p.triangle(-arrowSize, arrowSize / 2, -arrowSize, -arrowSize / 2, 0, 0);
        p.pop();
    };

    // Handle window resize
    p.windowResized = function () {
        p.resizeCanvas(p.windowWidth > 800 ? 800 : p.windowWidth - 40, 500);
    };
};

// Initialize the p5.js sketch
new p5(energyEquivalenceSketch);
